import { useState, useEffect, useRef } from "react";
import gmailMecoLogoDark from "@images/gmail_integration_dark.png";
import outlookMecoLogoDark from "@images/outlook_integration_dark.png";
import { ReactComponent as BenefitWand } from "@images/gmail_benefit_wand.svg";
import { ReactComponent as BenefitPrivacy } from "@images/gmail_benefit_privacy.svg";
import { kStringConstants } from "@utils/constants/StringConstants";
import { kErrorConstants } from "@utils/constants/ErrorConstants";
import LoadingView, { LoadingState } from "@components/common/LoadingView";
import BenefitView, { BenefitViewState } from "../common/integration-common/BenefitView";
import OauthSessionManager from "@utils/managers/oauth/OauthSessionManager";
import Alert, { AlertState } from "@components/common/Alert";
import OauthPrivacyPopup, { OauthPrivacyPopupState } from "../common/integration-common/OauthPrivacyPopup";
import { NLMailboxProfile, OnboardingProfile } from "@models/Models";
import * as analyticsManager from "@utils/managers/AnalyticsManager";
import ParticlesBackground from "@components/common/ParticlesBackground";
import { useAuth } from "@providers/AuthContext";
import { useOnboardingProfile } from "@providers/OnboardingContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import suitcase from "@images/suitcase.png";
import thunder from "@images/thunder.png";
import vacuum from "@images/vacuum.png";
import arrow from "@images/arrow.png";
import { ReactComponent as Magnifier } from "@images/magnifier.svg";
import { ReactComponent as Inbox } from "@images/inbox.svg";
import { usePremium } from "@providers/PremiumContext";
import PaywallModal from "@components/common/PaywallModal";
import { kAnalyticsConstants } from "@utils/constants/AnalyticsConstants";

const OAuthIntegration = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const isAccount: boolean = (location.state?.isAccount || location.search.includes("is_account")) ?? false;
	const isOutlook: boolean = location.pathname === "/get-started/connect-outlook";
    const { isPremium } = usePremium();

	const [privacyPopupState, setPrivacyPopupState] = useState(OauthPrivacyPopupState.hidden);
	const [showPaywall, setShowPaywall] = useState<boolean>(false);
	const [alertState, setAlertState] = useState<AlertState>({
		isShow: null,
		title: null,
		message: null,
		actionButton: null,
		dismissButton: null,
		actionButtonAction: undefined,
	});
	const { setAuthUser } = useAuth();
	const { onboardingProfile, setOnboardingProfile } = useOnboardingProfile();
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: false,
	});
	const [currentSection, setCurrentSection] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const mailboxTypeName = isOutlook ? "Outlook" : "Gmail";

	const benefits: BenefitViewState[] = [
		{
			icon: <Magnifier className="fill-force-primary-light w-[30px]" />,
			title: `Meco finds your newsletters in ${mailboxTypeName}`,
			description: "We instantly find all your newsletters so you can choose which ones to add to Meco.",
		},
		{
			icon: <Inbox className="fill-force-primary-light w-[30px]" />,
			title: "Organized in a dedicated space",
			description: "Your newsletters get their own dedicated app with clean formatting and easy browsing.",
		},
		{
			icon: <BenefitWand className="fill-force-primary-light w-[30px]" />,
			title: "Your inbox stays clean & private",
			description: "Newsletters move to Meco while your personal emails stay exactly where they are.",
		},
	];

	// Define the benefit sections
	const benefitSections = [
		{
			subtitle: "All your newsletters in one place",
			description: `Connect your ${mailboxTypeName} for a fast setup and a seamless reading experience.`,
			image: suitcase,
			color: "!bg-blue-300/10",
		},
		{
			subtitle: "Smart organization & AI summaries",
			description: "We'll automatically organize your newsletters and create smart summaries on demand, so you can read however works best for you.",
			image: thunder,
			color: "!bg-purple-300/10",
		},
		{
			subtitle: "Declutter your inbox",
			description: `Selected newsletters are moved to a special ${mailboxTypeName === "Gmail" ? "label" : "folder"} in ${mailboxTypeName}, keeping your main inbox clean.`,
			image: vacuum,
			color: "!bg-green-300/10",
		},
		{
			subtitle: "Nothing is permanent",
			description: `Easily move newsletters back to your ${mailboxTypeName} inbox anytime, you're always in control.`,
			image: arrow,
			color: "!bg-indigo-300/10",
		},
	];

	// Define the final integration section
	const finalSection = {
		title: `Meco works seamlessly with ${mailboxTypeName}`,
		image: isOutlook ? outlookMecoLogoDark : gmailMecoLogoDark,
		benefits: benefits,
	};

	useEffect(() => {
		if (isAccount) {
			scrollToSection(benefitSections.length, true);
		}
		analyticsManager.recordEvent("OAU - Presented", { is_onboarding: !isAccount, mailbox_type: mailboxTypeName });		
	}, []);

	// Handle scroll to update current section
	useEffect(() => {
		const handleScroll = () => {
			if (!containerRef.current) return;

			const container = containerRef.current;
			const scrollTop = container.scrollTop;
			const sectionHeight = container.clientHeight;
			const newSection = Math.round(scrollTop / sectionHeight);

			setCurrentSection(Math.min(newSection, benefitSections.length));
		};

		const container = containerRef.current;
		if (container) {
			container.addEventListener("scroll", handleScroll);
			return () => container.removeEventListener("scroll", handleScroll);
		}
	}, []);

	// Scroll to specific section
	const scrollToSection = (sectionIndex: number, noAnimation: boolean = false) => {
		if (!containerRef.current) return;

		const container = containerRef.current;
		const sectionHeight = container.clientHeight;
		container.scrollTo({
			top: sectionIndex * sectionHeight,
			behavior: noAnimation ? "instant" : "smooth"
		});
	};

	const integrateGmailOnClick = () => {
		analyticsManager.recordEvent("OAU - Integrate Tapped", { is_onboarding: !isAccount, mailbox_type: mailboxTypeName });
		setPrivacyPopupState(OauthPrivacyPopupState.visible);
	};

	const integrateWithGmail = async () => {

		if (!isPremium()) {
			setPrivacyPopupState(OauthPrivacyPopupState.hidden);
			setShowPaywall(true);
			return;
		}

		analyticsManager.recordEvent("OAU - Integration - Started", { is_onboarding: !isAccount, mailbox_type: mailboxTypeName });
		setPrivacyPopupState(OauthPrivacyPopupState.hidden);

		switch (mailboxTypeName) {
			case "Gmail":
				OauthSessionManager.sharedInstance()
					.integrateWithGmail()
					.then(([profile, mailboxProfileId]) => {
						setLoadingState({ isLoading: false });
						setAuthUser(profile);
						const mailboxProfile = profile.mailbox_profiles.find((x) => x.id === mailboxProfileId);
						if (mailboxProfile) {
							oauthComplete(mailboxProfile);
						}
						throw kErrorConstants.commonBackendError;
					})
					.catch((error) => {
						setLoadingState({ isLoading: false });
						handleSignInError(error);
					});
				break;
			case "Outlook":
				OauthSessionManager.sharedInstance()
					.integrateWithOutlook()
					.then(([profile, mailboxProfileId]) => {
						setLoadingState({ isLoading: false });
						setAuthUser(profile);
						const mailboxProfile = profile.mailbox_profiles.find((x) => x.id === mailboxProfileId);
						if (mailboxProfile) {
							oauthComplete(mailboxProfile);
						}
						throw kErrorConstants.commonBackendError;
					})
					.catch((error) => {
						setLoadingState({ isLoading: false });
						handleSignInError(error);
					});
				break;
			default:
				break;
		}
	};

	const oauthComplete = (mailboxProfile: NLMailboxProfile) => {
		var tempOnboardingProfile = isAccount ? new OnboardingProfile() : { ...onboardingProfile! };
		tempOnboardingProfile.persistence.mailboxProfile = mailboxProfile;
		tempOnboardingProfile.persistence.hasSeenPaywall = false;
		setOnboardingProfile(tempOnboardingProfile);
		analyticsManager.recordEvent("OAU - Integration - Completed", { is_onboarding: !isAccount, mailbox_profile_id: tempOnboardingProfile.persistence.mailboxProfile?.id, mailbox_profile_email: tempOnboardingProfile.persistence.mailboxProfile?.email_address, mailbox_type: mailboxTypeName });
		navigate("/get-started/newsletter-picker", { state: { isAccount: isAccount } });
	};

	const handleSignInError = (error) => {
		var alertTitle = kStringConstants.Common.errorAlertTitle;
		var alertMessage = kStringConstants.Common.errorAlertMessage;

		if (error == kErrorConstants.oauthIntegration.userCancelled) {
			analyticsManager.recordEvent("OAU - Integration - User Cancelled Flow", { is_onboarding: !isAccount, error: error.message ?? "", mailbox_type: mailboxTypeName });
		} else {
			analyticsManager.recordEvent("OAU - Integration - Error", { is_onboarding: !isAccount, error: error.message ?? "", mailbox_type: mailboxTypeName });
		}

		if (error === kErrorConstants.oauthIntegration.permissionError) {
			alertTitle = `Please allow ${mailboxTypeName} permissions`;
			alertMessage = "We require these permissions to add and manage newsletters with Meco. We designed Meco with your privacy in mind. Your emails remain private and are only accessible by you.";
		}

		if (error === kErrorConstants.oauthIntegration.alreadyIntegratedAccount) {
			alertTitle = `${mailboxTypeName} already in use`;
			alertMessage = `This ${mailboxTypeName} address is already associated with another Meco account. ${mailboxTypeName === "Outlook" ? "An Outlook" : "A Gmail"} can only be connected to one Meco account at a time.`;
		}

		if (error === kErrorConstants.oauthIntegration.accountMismatch) {
			alertTitle = "Incorrect Gmail address";
			alertMessage = "The address you selected did not match the email above. Please retry with the correct Gmail.";
		}

		setAlertState({ isShow: true, title: alertTitle, message: alertMessage, actionButton: "OK", dismissButton: null, actionButtonAction: undefined });
	};

	return (
		<div className="relative h-[100dvh] overflow-hidden">
			<PaywallModal isShow={showPaywall} onClose={() => setShowPaywall(false)} analyticsReference={ isOutlook ? kAnalyticsConstants.Reference.Paywall.outlookIntegration : kAnalyticsConstants.Reference.Paywall.gmailIntegration} isOnboarding={!isAccount} />
			<LoadingView loadingState={loadingState} />
			<Alert alertState={alertState} setAlertState={setAlertState} />
			<OauthPrivacyPopup setIsShow={() => setPrivacyPopupState(OauthPrivacyPopupState.hidden)} integrationType={mailboxTypeName} popupState={privacyPopupState} isOnboarding={!isAccount} agreeOnClick={() => integrateWithGmail()} />
			<ParticlesBackground />

			{/* Main scrollable container */}
			<div ref={containerRef} className="h-full overflow-y-auto scroll-smooth snap-y snap-mandatory">
				{benefitSections.map((section, index) => (
					<div key={index} className="h-full snap-start flex items-center justify-center p-3">
						<div className={`meco_container text-center max-w-[1080px] py-[40px] md:p-[60px] ${section.color}`}>
							<div className="flex flex-col items-center gap-8 max-w-[400px] md:max-w-none">
								<div className="text-white font-medium font-primary text-3xl text-center md:leading-10 leading-9">Meco works seamlessly with {mailboxTypeName}</div>

								<div className="flex flex-col gap-4 items-center">
									<img src={section.image} className="w-[180px]" />
									<div className="text-white font-medium font-primary text-xl text-center">{section.subtitle}</div>

									<div className="text-white/50 font-primary text-base text-center max-w-[500px]">{section.description}</div>
								</div>
							</div>
						</div>
					</div>
				))}

				{/* Final section with original styling */}
				<div className="h-full snap-start flex items-center justify-center p-3">
					<div className="meco_container text-center max-w-[1080px] py-[40px] md:p-[60px] md:pb-[40px]">
						<div className="flex flex-col-reverse md:flex-row gap-5 max-w-[400px] md:max-w-none">
							<div className="w-full md:w-1/2 flex flex-col">
								<div className="flex flex-col gap-3">
									{finalSection.benefits.map((benefit) => (
										<BenefitView key={benefit.title} benefit={benefit} />
									))}
									<div className="flex flex-col gap-0 md:hidden">
										<button className="flex m-auto cta-button w-full shadow-2xl mt-4" onClick={integrateGmailOnClick}>
											Connect with {mailboxTypeName}
										</button>
										{isAccount ? (
											<Link to="/settings">
												<button className="text-force-primary-light font-medium font-primary text-base focus:outline-none whitespace-nowrap mt-3">Back</button>
											</Link>
										) : (
											<Link to="/get-started/meco-mailbox">
												<button className="text-force-primary-light font-medium font-primary text-base focus:outline-none whitespace-nowrap mt-3">Not now</button>
											</Link>
										)}
									</div>
								</div>
							</div>
							<div className="w-full md:w-1/2 flex flex-col items-center justify-center gap-4">
								<div className="text-white font-medium font-primary text-3xl text-center md:leading-10 leading-9">{finalSection.title}</div>

								<img src={finalSection.image} className="w-[250px]" />
								<div className="flex flex-col gap-2 w-full items-center mt-3">
									<div className="flex flex-row gap-1">
										<BenefitPrivacy className="fill-success-green w-[10px]" />
										<div className="text-success-green font-medium font-primary text-xs">{mailboxTypeName === "Gmail" ? "Google verified & privacy protected." : "Privacy & security protected."}</div>
									</div>
									<button className="hidden md:flex cta-button w-full shadow-2xl" onClick={integrateGmailOnClick}>
										Connect with {mailboxTypeName}
									</button>
								</div>
							</div>
						</div>
						<div className="w-full hidden md:block">
							<div className="h-[1px] w-full bg-force-primary-light/20 mt-5"></div>
							<div className={`flex ${isAccount ? "justify-start" : "justify-end"}`}>
								<Link to={isAccount ? "/settings" : "/get-started/integrations"}>
									<button className="text-force-primary-light font-medium font-primary text-base focus:outline-none rounded-xl p-2 px-3 py-2 items-center bg-white/20 hover:bg-brand-blue/80 transition ease-in-out whitespace-nowrap mt-3">{isAccount ? "Back" : "Other available options"}</button>
								</Link>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Navigation circles */}
			<div className="fixed right-6 top-1/2 transform -translate-y-1/2 flex flex-col gap-3 z-10">
				{[...benefitSections, finalSection].map((_, index) => (
					<button key={index} onClick={() => scrollToSection(index)} className={`w-3 h-3 rounded-full transition-all duration-300 ${currentSection === index ? "bg-white scale-125" : "bg-white/30 hover:bg-white/50"}`} aria-label={`Go to section ${index + 1}`} />
				))}
			</div>

			{/* Animated down arrow - only show if not on last section */}
			{currentSection < benefitSections.length && (
				<div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-10">
					<div className="animate-bounce">
						<div className="w-6 h-6 border-l-2 border-b-2 border-white transform -rotate-45"></div>
					</div>
				</div>
			)}
		</div>
	);
};

export default OAuthIntegration;
